import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText, {
  Opts as ButtonScaleViewWithTextOpts,
} from 'src/lib/ui/components/ButtonScaleViewWithText';
import View, { ViewOpts } from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import Scroll from '../../components/shared/Scroll';
import { ImageView } from '@play-co/timestep-core/ui';
import TabbedView from '../../components/shared/tabs/TabbedView';
import { PetSpeciesID } from 'src/replicant/pet';
import { LangBitmapOpts } from 'src/lib/ui/components/LangBitmapFontTextView';
import { ImageViewOpts } from '@play-co/timestep-core/lib/ui/ImageView';
import { VisitFriendTile } from './VisitFriendTile';
import { Feature } from 'src/replicant/analytics';
import StateObserver, { user } from 'src/StateObserver';
import { VisitorData } from 'src/replicant/visit/visit.asyncGetters';
import { withLoading } from 'src/lib/utils';
import { TabOpts } from 'src/game/components/shared/tabs/Tabs';
import { FriendshipRequest } from 'src/replicant/friends/friends.ruleset';
import { FriendshipRequestTile } from './FriendshipRequestTile';
import { loadVisitFriendsList } from '../visit.sequence';
import {
  trackFriendVisitTargetListOpenEvent,
  trackVisitorListOpenEvent,
} from '../visit.analytics';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { startInviteFriendsSequence } from 'src/game/social/social.sequence';
import { AvatarFrameId } from 'src/replicant/profile/frames';

import { AssetGroup } from '@play-co/timestep-core';
import { fetchRecentVisitors } from 'src/game/friends/friends.utils';
import NotificationBadge from 'src/game/components/shared/NotificationBadge';
import { duration } from 'src/replicant/utils/duration';

type SocialFriend = {
  id: string;
  name?: string;
  photo?: string;
  frame: AvatarFrameId;
  species: PetSpeciesID;
  lastLogin: number;
  canAcceptFriendRequest: boolean;
};
type VisitTab = 'friends' | 'visitors';
type CloseHandler = (result?: {
  // Selected friend's ID
  friendId: string;
  // Currently active tab on closing
  selectedTab: VisitTab;
}) => void;

namespace style {
  export const popup = {
    zIndex: 10000,
    width: uiConfig.popups.buruburu.width,
    height: 1015,
    darkerBg: false,
    titleIcon: { image: 'assets/ui/visit/icons/visitfriend_icon.png' },
  };

  export const banner: ViewOpts = {
    backgroundColor: '#FFF3D9',
    x: 7,
    height: 103,
    y: 90,
  };
  banner.width = popup.width - 2 * banner.x!;

  export const bannerBorder: ViewOpts = {
    backgroundColor: '#fdc381',
    width: banner.width,
    height: 3,
  };

  export const bannerIcon: ImageViewOpts = {
    image: 'assets/ui/visit/icons/visitfriend_icon_display_banner.png',
    y: 8,
    width: 152,
    height: 98,
  };

  export const message: LangBitmapOpts = {
    size: 20,
    width: 382,
    height: banner.height,
    x: 175,
    y: banner.y,
    zIndex: 1,
    align: 'left',
    verticalAlign: 'center',
  };

  export const tab: ViewOpts = {
    backgroundColor: '#fdc381',
  };

  export const cancelButton: Omit<ButtonScaleViewWithTextOpts, 'tracking'> = {
    ...uiConfig.buttons.cancel,
    labelOffsetY: -1,
    fontSize: 46,
    font: 'Body',
    x: popup.width / 2,
    y: popup.height - 25 - 100 / 2,
    width: 259,
    height: 100,
    centerOnOrigin: true,
  };
}

export default class PopupVisitFriend extends PopupBasic {
  private tabs: TabbedView;
  private friendsList: Scroll<SocialFriend, VisitFriendTile>;
  private visitorsList: Scroll<SocialFriend, VisitFriendTile>;
  private requestsList!: Scroll<FriendshipRequest, FriendshipRequestTile>;
  private visitorsListPromise: Promise<VisitorData[]> | null = null;
  private buttonCancel: ButtonScaleViewWithText;
  private activeTab!: VisitTab;
  private close: CloseHandler;
  private visitorsBadge: NotificationBadge | null = null;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      ...style.popup,
      superview: opts.superview,
      close: () => opts.close(),
      tracking: {
        feature: Feature.VISIT._,
        $subFeature: Feature.VISIT.POPUP_VISIT_FRIEND._,
      },
    });

    this.close = opts.close;
    this.buttonClose.removeFromSuperview();

    const banner = this.box.addSubview(new View(style.banner));
    banner.addSubview(
      new View({
        ...style.bannerBorder,
        y: -style.bannerBorder.height!,
      }),
    );
    banner.addSubview(
      new View({
        ...style.bannerBorder,
        y: style.message.height,
      }),
    );
    banner.addSubview(new ImageView(style.bannerIcon));
    this.message.updateOpts(style.message);

    this.tabs = new TabbedView({
      tabsFillWidth: true,
      tabs: this.createTabs(),
      alignment: 'left',
      superview: this.box,
      rect: {
        x: 30,
        y: 270,
        width: this.box.style.width - 60,
        height: 600,
      },
    });

    const views = this.tabs.getViews();
    views.forEach((view) => view.updateOpts(style.tab));
    const [friendsTab, visitorsTab] = views;

    this.friendsList = new Scroll({
      superview: friendsTab,
      createItem: (superview, idx, friend) =>
        this.createItem(superview, idx, friend, 'friends'),
      recycle: true,
      setData: this.setItemData.bind(this),
    });

    this.visitorsList = new Scroll({
      superview: visitorsTab,
      createItem: (superview, idx, friend) =>
        this.createItem(superview, idx, friend, 'visitors'),
      recycle: true,
      setData: this.setItemData.bind(this),
    });

    this.buttonCancel = this.box.addSubview(
      new ButtonScaleViewWithText({
        ...style.cancelButton,
        localeText: () => i18n('basic.close'),
        onClick: async () => {
          sounds.sfx.play('cancel');
          return opts.close();
        },
        tracking: {
          feature: Feature.VISIT._,
          $subFeature: Feature.VISIT.POPUP_VISIT_FRIEND.CLOSE,
        },
      }),
    );

    // Red dot badge on Visitors tab for unvisited recent visitors
    const visitorsTabButton = this.tabs.getTabs()[1].getView();
    this.visitorsBadge = new NotificationBadge({
      superview: visitorsTabButton,
      size: 20,
      x: visitorsTabButton.style.width - 40,
      y: -1,
      font: 'Body',
      fontSize: 1,
      textColor: 'white',
      style: 'red',
    });
    this.visitorsBadge.hide();
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      'assets/ui/visit/icons/visitfriend_icon.png',
      'assets/ui/visit/icons/visitfriend_icon_display_banner.png',
    ]);
  }

  public init(opts: { selectedTab?: VisitTab }) {
    super.init({
      title: i18n('friendsVisit.popup.title'),
      message: i18n('friendsVisit.popup.message'),
    });

    void this.selectTab(opts.selectedTab ?? 'friends');

    this.updateVisitorsBadge();
  }

  private updateVisitorsBadge() {
    if (!this.visitorsBadge) {
      return;
    }

    const { recentVisitsFrom, recentVisitsTo } = user().visit;
    const thirtyDaysAgo = StateObserver.now() - duration({ days: 30 });

    const hasUnvisitedBack = Object.entries(recentVisitsFrom).some(
      ([visitorId, visitTimestamp]) => {
        if (visitTimestamp < thirtyDaysAgo) return false;
        const visitedBackAt = recentVisitsTo[visitorId];
        return !visitedBackAt || visitedBackAt < visitTimestamp;
      },
    );

    if (hasUnvisitedBack) {
      this.visitorsBadge.show();
    } else {
      this.visitorsBadge.hide();
    }
  }

  /**
   * Create tabs for TabbedView
   */
  private createTabs(): TabOpts[] {
    const defaultTabs = [
      {
        label: () => i18n('friendsVisit.popup.tab-friends'),
        tracking: {
          feature: Feature.VISIT._,
          $subFeature: Feature.VISIT.POPUP_VISIT_FRIEND.TAB_FRIENDS,
        },
        onSelect: () => {
          this.selectTab('friends');
        },
      },
      {
        label: () => i18n('friendsVisit.popup.tab-visitors'),
        tracking: {
          feature: Feature.VISIT._,
          $subFeature: Feature.VISIT.POPUP_VISIT_FRIEND.TAB_VISITORS,
        },
        onSelect: () => {
          this.selectTab('visitors');
        },
      },
    ];

    return defaultTabs;
  }

  private async selectTab(tab: VisitTab) {
    if (tab === 'visitors') {
      this.activeTab = 'visitors';
      this.tabs.setProps({ selectedTabIndex: 1 });

      const data = await withLoading(() => fetchRecentVisitors().get(), {
        feature: Feature.VISIT._,
        subFeature: Feature.VISIT.RECENT_VISITORS,
      });

      this.visitorsList.setItems(data);

      // Track visitors tab open event
      const { recentVisitsTo } = user().visit;
      const recentVisitors = data.filter(
        (visitor) =>
          !visitor.isOnline &&
          visitor.lastVisit > (recentVisitsTo[visitor.id] ?? 0),
      ).length;
      trackVisitorListOpenEvent({
        visitorCount: data.length,
        recentVisitors,
      });

      return;
    }

    this.activeTab = 'friends';
    this.tabs.setProps({ selectedTabIndex: 0 });

    const data = await withLoading(() => this.loadFriendsList(), {
      feature: Feature.VISIT._,
      subFeature: Feature.VISIT.FRIENDS_LIST,
    });

    if (data.length === 0) {
      this.close();

      // At this point if the player has no friends, we should resolve the "want to go out" status effect.
      await StateObserver.invoke.resolveWantOut();

      const invite = await openPopupPromise('popupNoFriends', {});
      if (invite) {
        await startInviteFriendsSequence(
          Feature.VISIT._,
          Feature.VISIT.NO_FRIENDS_INVITE,
        );
      }
      return;
    }
    this.friendsList.setItems(data);
  }

  private createItem(
    superview: View,
    _: number,
    friend: SocialFriend,
    tab: VisitTab,
  ) {
    return superview.addSubview(
      new VisitFriendTile({
        friend,
        tab,
        onClick: async () =>
          this.close({ friendId: friend.id, selectedTab: this.activeTab }),
      }),
    );
  }

  private setItemData(_: number, friend: SocialFriend, view: VisitFriendTile) {
    view.setData(friend, async () =>
      this.close({ friendId: friend.id, selectedTab: this.activeTab }),
    );
  }

  private loadFriendsList() {
    const friendListPromise = loadVisitFriendsList();

    friendListPromise.then((friends) => {
      const numLineFriends = friends.filter((friend) => friend.isFriend).length;
      const numOtherPlayers = friends.length - numLineFriends;

      // Track how many nonFriends and friends we found
      trackFriendVisitTargetListOpenEvent({
        numLineFriends,
        numOtherPlayers,
      });
    });

    return friendListPromise;
  }
}
